import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';

export type ConversationStarter = {
	starter: string;
	isSummarize?: boolean;
	isShowMore?: boolean;
	isShowLess?: boolean;
};

export interface ConversationStartersProps {
	conversationStarters: ConversationStarter[];
	onConversationStarterClick: (starter: ConversationStarter, index: number) => void;
	url: string;
}

const messages = {
	summarize: {
		id: 'search-ai.conversation-starters.summarize',
		defaultMessage: 'Summarize',
		description: 'Button text for AI summarize action',
	},
	showLess: {
		id: 'search-ai.conversation-starters.show-less',
		defaultMessage: 'Show less',
		description: 'Button text for showing less conversation starters',
	},
	showMore: {
		id: 'search-ai.conversation-starters.show-more',
		defaultMessage: 'Show more',
		description: 'Button text for showing more conversation starters',
	},
};

export const ConversationStarters: React.FC<ConversationStartersProps> = ({
	conversationStarters,
	onConversationStarterClick,
	url,
}) => {
	const { formatMessage } = useIntl();
	const publish = usePublish('ai-mate');
	const [showMore, setShowMore] = useState<boolean>(false);

	const handleConversationStarterClick = useCallback(
		(starter: ConversationStarter, index: number) => {
			publish({
				type: 'browser-context-payload',
				source: 'search-result',
				data: {
					context: {
						browserUrl: url,
					},
				},
			});

			publish({
				type: 'chat-new',
				source: 'search-result',
				data: {
					name: starter.starter,
					prompt: `${starter.starter} ${url}`,
					dialogues: [],
				},
			});

			onConversationStarterClick(starter, index);
		},
		[publish, onConversationStarterClick, url],
	);

	const handleShowMoreClick = useCallback(() => {
		setShowMore((s) => !s);

		const analyticsPayload = {
			starter: '', // Empty since this isn't a real conversation starter
			[showMore ? 'isShowLess' : 'isShowMore']: true,
		};

		onConversationStarterClick(analyticsPayload, -1);
	}, [showMore, onConversationStarterClick]);

	const summarizeText = formatMessage(messages.summarize);

	// Create array with summarize button first, then conversation starters
	const allItems = [
		{ starter: summarizeText, isSummarize: true, originalIndex: 0 },
		...conversationStarters.map((starter, index) => ({
			...starter,
			isSummarize: false,
			originalIndex: index + 1,
		})),
	];

	const visibleItems = allItems.slice(0, 2);
	const shouldShowExpandButton = allItems.length > 2;

	return (
		<Inline space="space.100" alignBlock="start" shouldWrap>
			{(showMore ? allItems : visibleItems).map((item) => (
				<Box key={item.starter}>
					<Button
						iconBefore={RovoChatIcon}
						onClick={() => {
							const analyticsPayload = {
								starter: item.starter,
								isSummarize: item.isSummarize || false,
							};
							handleConversationStarterClick(analyticsPayload, item.originalIndex);
						}}
					>
						{item.starter}
					</Button>
				</Box>
			))}
			{shouldShowExpandButton && (
				<Box>
					<IconButton
						onClick={handleShowMoreClick}
						icon={showMore ? ChevronUpIcon : ChevronDownIcon}
						label={showMore ? formatMessage(messages.showLess) : formatMessage(messages.showMore)}
					/>
				</Box>
			)}
		</Inline>
	);
};
